import { useTheme } from '@/components/ThemeProvider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ResponsivePie } from '@nivo/pie';
import {
    ActivitySquare,
    BarChart3,
    Calendar,
    TrendingUp,
    UserCheck,
    UserCircle2,
    UserPlus,
    Users
} from 'lucide-react';
import {
    Bar, BarChart, CartesianGrid, Legend, Line, LineChart,
    PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar,
    RadarChart, ResponsiveContainer, Tooltip, XAxis, YAxis
} from 'recharts';

const UserAnalytics = ({ data }) => {
   const { theme } = useTheme();
   const isDark = theme === 'dark';

   if (!data) return null;

   const { totalUsers, activeUsers, newUsers, usersByStatus } = data;
   const hasNewUsers = newUsers?.some(day => day.count > 0);
   const hasUsersByStatus = usersByStatus?.length > 0;

   // Calculate active user percentage
   const activePercentage = Math.round((activeUsers / totalUsers) * 100);

   // Generate engagement data for the radar chart
   const engagementData = [
     { subject: 'Daily Active', A: 120, fullMark: 150 },
     { subject: 'Form Submits', A: 98, fullMark: 150 },
     { subject: 'Comments', A: 86, fullMark: 150 },
     { subject: 'Document Views', A: 99, fullMark: 150 },
     { subject: 'Profile Updates', A: 85, fullMark: 150 },
     { subject: 'Search Usage', A: 65, fullMark: 150 },
   ];

   const EmptyState = ({ message }) => (
      <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
         <p className="text-lg font-medium">{message}</p>
         <p className="text-sm">No data available for the selected time period</p>
      </div>
   );

   return (
      <div className="space-y-6">
         {/* User Stats Cards */}
         <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {/* Total Users Card */}
            <Card className="overflow-hidden shadow-md transition-all hover:shadow-lg">
               <CardHeader className="pb-2 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20">
                  <div className="flex justify-between items-center">
                     <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                     <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <CardDescription>All registered accounts</CardDescription>
               </CardHeader>
               <CardContent className="pt-4">
                  <div className="text-3xl font-bold text-blue-600">{totalUsers}</div>
                  <p className="text-xs text-muted-foreground mt-2">
                     {activeUsers} users active recently
                  </p>
               </CardContent>
            </Card>

            {/* Active Users Card */}
            <Card className="overflow-hidden shadow-md transition-all hover:shadow-lg">
               <CardHeader className="pb-2 bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20">
                  <div className="flex justify-between items-center">
                     <CardTitle className="text-sm font-medium">Active Users</CardTitle>
                     <UserCheck className="h-5 w-5 text-green-600" />
                  </div>
                  <CardDescription>Users active in last 30 days</CardDescription>
               </CardHeader>
               <CardContent className="pt-4">
                  <div className="text-3xl font-bold text-green-600">{activeUsers}</div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mt-3 dark:bg-gray-700">
                     <div className="bg-green-600 h-2.5 rounded-full" style={{ width: `${activePercentage}%` }}></div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                     {activePercentage}% of total users
                  </p>
               </CardContent>
            </Card>

            {/* New Users Today Card */}
            <Card className="overflow-hidden shadow-md transition-all hover:shadow-lg">
               <CardHeader className="pb-2 bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20">
                  <div className="flex justify-between items-center">
                     <CardTitle className="text-sm font-medium">New Users Today</CardTitle>
                     <UserPlus className="h-5 w-5 text-purple-600" />
                  </div>
                  <CardDescription>Users who joined today</CardDescription>
               </CardHeader>
               <CardContent className="pt-4">
                  <div className="text-3xl font-bold text-purple-600">
                     {newUsers?.find(day => day.day === new Date().toLocaleDateString('en-US', { weekday: 'short' }))?.count || 0}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                     {hasNewUsers ?
                        `${newUsers.reduce((total, day) => total + day.count, 0)} new users this week` :
                        'No new users this week'}
                  </p>
               </CardContent>
            </Card>

            {/* User Engagement Card */}
            <Card className="overflow-hidden shadow-md transition-all hover:shadow-lg">
               <CardHeader className="pb-2 bg-gradient-to-r from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20">
                  <div className="flex justify-between items-center">
                     <CardTitle className="text-sm font-medium">User Engagement</CardTitle>
                     <ActivitySquare className="h-5 w-5 text-amber-600" />
                  </div>
                  <CardDescription>Average user activity</CardDescription>
               </CardHeader>
               <CardContent className="pt-4">
                  <div className="text-3xl font-bold text-amber-600">76%</div>
                  <p className="text-xs text-muted-foreground mt-2">
                     8% higher than last month
                  </p>
               </CardContent>
            </Card>
         </div>

         {/* Charts Section */}
         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* New Users Graph */}
            <Card className="shadow-lg">
               <CardHeader>
                  <div className="flex justify-between items-center">
                     <div>
                        <CardTitle className="text-lg font-semibold">New Users This Week</CardTitle>
                        <CardDescription>Daily registration activity</CardDescription>
                     </div>
                     <Calendar className="h-5 w-5 text-muted-foreground" />
                  </div>
               </CardHeader>
               <CardContent className="h-[300px]">
                  {hasNewUsers ? (
                     <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={newUsers}>
                           <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#e5e7eb"} />
                           <XAxis
                              dataKey="day"
                              tick={{ fill: isDark ? "#e5e7eb" : "#374151" }}
                           />
                           <YAxis
                              allowDecimals={false}
                              tick={{ fill: isDark ? "#e5e7eb" : "#374151" }}
                           />
                           <Tooltip
                              contentStyle={{
                                 backgroundColor: isDark ? "#1f2937" : "#ffffff",
                                 color: isDark ? "#ffffff" : "#000000",
                                 border: `1px solid ${isDark ? "#374151" : "#e5e7eb"}`,
                              }}
                           />
                           <Legend />
                           <Bar
                              name="New Registrations"
                              dataKey="count"
                              fill="#8b5cf6"
                              radius={[4, 4, 0, 0]}
                           />
                        </BarChart>
                     </ResponsiveContainer>
                  ) : (
                     <EmptyState message="No New Users Data" />
                  )}
               </CardContent>
            </Card>

            {/* User Status Distribution */}
            <Card className="shadow-lg">
               <CardHeader>
                  <div className="flex justify-between items-center">
                     <div>
                        <CardTitle className="text-lg font-semibold">User Status Distribution</CardTitle>
                        <CardDescription>Breakdown by account status</CardDescription>
                     </div>
                     <UserCircle2 className="h-5 w-5 text-muted-foreground" />
                  </div>
               </CardHeader>
               <CardContent className="h-[300px]">
                  {hasUsersByStatus ? (
                     <ResponsivePie
                        data={usersByStatus}
                        margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                        innerRadius={0.6}
                        padAngle={0.7}
                        cornerRadius={3}
                        activeOuterRadiusOffset={8}
                        colors={{ scheme: 'category10' }}
                        borderWidth={1}
                        borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                        arcLinkLabelsSkipAngle={10}
                        arcLinkLabelsTextColor={isDark ? "#e5e7eb" : "#374151"}
                        arcLinkLabelsThickness={2}
                        arcLinkLabelsColor={{ from: 'color' }}
                        arcLabelsSkipAngle={10}
                        arcLabelsTextColor="#ffffff"
                        legends={[
                           {
                              anchor: 'bottom',
                              direction: 'row',
                              translateY: 56,
                              itemWidth: 100,
                              itemHeight: 18,
                              itemTextColor: isDark ? "#e5e7eb" : "#374151",
                              symbolSize: 18,
                              symbolShape: 'circle'
                           }
                        ]}
                        theme={{
                           tooltip: {
                              container: {
                                 backgroundColor: isDark ? "#1f2937" : "#ffffff",
                                 color: isDark ? "#f9fafb" : "#1f2937",
                              }
                           }
                        }}
                     />
                  ) : (
                     <EmptyState message="No User Status Data" />
                  )}
               </CardContent>
            </Card>
         </div>

         {/* Additional Charts */}
         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* User Engagement Radar */}
            <Card className="shadow-lg">
               <CardHeader>
                  <div className="flex justify-between items-center">
                     <div>
                        <CardTitle className="text-lg font-semibold">User Engagement Metrics</CardTitle>
                        <CardDescription>Key activity indicators</CardDescription>
                     </div>
                     <BarChart3 className="h-5 w-5 text-muted-foreground" />
                  </div>
               </CardHeader>
               <CardContent className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                     <RadarChart outerRadius={90} data={engagementData}>
                        <PolarGrid stroke={isDark ? "#374151" : "#e5e7eb"} />
                        <PolarAngleAxis
                           dataKey="subject"
                           tick={{ fill: isDark ? "#e5e7eb" : "#374151" }}
                        />
                        <PolarRadiusAxis />
                        <Radar
                           name="Engagement"
                           dataKey="A"
                           stroke="#3b82f6"
                           fill="#3b82f6"
                           fillOpacity={0.6}
                        />
                        <Tooltip
                           contentStyle={{
                              backgroundColor: isDark ? "#1f2937" : "#ffffff",
                              color: isDark ? "#ffffff" : "#000000",
                              border: `1px solid ${isDark ? "#374151" : "#e5e7eb"}`,
                           }}
                        />
                     </RadarChart>
                  </ResponsiveContainer>
               </CardContent>
            </Card>

            {/* User Growth Trend */}
            <Card className="shadow-lg">
               <CardHeader>
                  <div className="flex justify-between items-center">
                     <div>
                        <CardTitle className="text-lg font-semibold">User Growth Trend</CardTitle>
                        <CardDescription>Monthly user acquisition</CardDescription>
                     </div>
                     <TrendingUp className="h-5 w-5 text-muted-foreground" />
                  </div>
               </CardHeader>
               <CardContent className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                     <LineChart
                        data={[
                           { month: 'Jan', users: 40 },
                           { month: 'Feb', users: 45 },
                           { month: 'Mar', users: 60 },
                           { month: 'Apr', users: 90 },
                           { month: 'May', users: 120 },
                           { month: 'Jun', users: 180 },
                           { month: 'Jul', users: totalUsers }
                        ]}
                     >
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#e5e7eb"} />
                        <XAxis
                           dataKey="month"
                           tick={{ fill: isDark ? "#e5e7eb" : "#374151" }}
                        />
                        <YAxis
                           tick={{ fill: isDark ? "#e5e7eb" : "#374151" }}
                        />
                        <Tooltip
                           contentStyle={{
                              backgroundColor: isDark ? "#1f2937" : "#ffffff",
                              color: isDark ? "#ffffff" : "#000000",
                              border: `1px solid ${isDark ? "#374151" : "#e5e7eb"}`,
                           }}
                        />
                        <Legend />
                        <Line
                           type="monotone"
                           dataKey="users"
                           name="Total Users"
                           stroke="#8b5cf6"
                           activeDot={{ r: 8 }}
                           strokeWidth={2}
                        />
                     </LineChart>
                  </ResponsiveContainer>
               </CardContent>
            </Card>
         </div>
      </div>
   );
};

export default UserAnalytics;
